/**
 * ViewAllLinksModal
 *
 * Displays all relationships where an endpoint is either source or target.
 * Groups relationships by source, connection type, and status for cleaner display.
 * Provides filtering, and actions (edit, delete) for each relationship.
 */

import { useTranslation } from 'react-i18next'
import { Link2, X } from 'lucide-react'
import type { CaseEndpoint, EndpointRelationship } from '../../types/case-container'
import { FilterBar } from './view-all-links-modal/FilterBar'
import { RelationshipGroupRow } from './view-all-links-modal/RelationshipGroupRow'
import { useViewAllLinksFilters } from './view-all-links-modal/use-view-all-links-filters'

interface ViewAllLinksModalProps {
  endpoint: CaseEndpoint
  relationships: EndpointRelationship[]
  endpoints: CaseEndpoint[]
  onClose: () => void
  onEdit: (relationship: EndpointRelationship) => void
  onDelete: (relationshipId: string) => void
  deleting?: boolean
}

interface ModalHeaderProps {
  hostname: string
  count: number
  onClose: () => void
}

function ModalHeader({ hostname, count, onClose }: ModalHeaderProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fuchsia-100 dark:bg-fuchsia-950/50">
          <Link2 className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('infrastructure.viewAllLinks.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('infrastructure.viewAllLinks.subtitle', { hostname, count })}
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface ModalFooterProps {
  filteredCount: number
  totalCount: number
  groupCount: number
  onClose: () => void
}

function ModalFooter({
  filteredCount,
  totalCount,
  groupCount,
  onClose,
}: ModalFooterProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('infrastructure.viewAllLinks.showingCount', {
          filtered: filteredCount,
          total: totalCount,
        })}
        {groupCount !== filteredCount && (
          <span className="ml-1">
            ({t('infrastructure.viewAllLinks.groupCount', { count: groupCount })})
          </span>
        )}
      </p>
      <button
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.close')}
      </button>
    </div>
  )
}

function EmptyState({ relevantCount }: { relevantCount: number }): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Link2 className="mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="text-slate-600 dark:text-slate-400">
        {relevantCount === 0
          ? t('infrastructure.viewAllLinks.noRelationships')
          : t('infrastructure.viewAllLinks.noMatchingFilters')}
      </p>
    </div>
  )
}

export function ViewAllLinksModal({
  endpoint,
  relationships,
  endpoints,
  onClose,
  onEdit,
  onDelete,
  deleting = false,
}: ViewAllLinksModalProps): React.ReactElement {
  const filters = useViewAllLinksFilters({ endpoint, relationships, endpoints })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <ModalHeader
          hostname={endpoint.hostname}
          count={filters.relevantRelationships.length}
          onClose={onClose}
        />
        <FilterBar
          searchTerm={filters.searchTerm}
          setSearchTerm={filters.setSearchTerm}
          directionFilter={filters.directionFilter}
          setDirectionFilter={filters.setDirectionFilter}
          statusFilter={filters.statusFilter}
          setStatusFilter={filters.setStatusFilter}
          typeFilter={filters.typeFilter}
          setTypeFilter={filters.setTypeFilter}
          uniqueStatuses={filters.uniqueStatuses}
          uniqueTypes={filters.uniqueTypes}
        />
        <div className="flex-1 overflow-auto px-6 py-4">
          {filters.groupedRelationships.length === 0 ? (
            <EmptyState relevantCount={filters.relevantRelationships.length} />
          ) : (
            <div className="space-y-3">
              {filters.groupedRelationships.map((group) => (
                <RelationshipGroupRow
                  key={group.key}
                  group={group}
                  isExpanded={filters.expandedGroups.has(group.key)}
                  onToggle={() => {
                    filters.toggleGroup(group.key)
                  }}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  deleting={deleting}
                />
              ))}
            </div>
          )}
        </div>
        <ModalFooter
          filteredCount={filters.filteredRelationships.length}
          totalCount={filters.relevantRelationships.length}
          groupCount={filters.groupedRelationships.length}
          onClose={onClose}
        />
      </div>
    </div>
  )
}
